/**
 * EXTRATO — despesas de viagem e vales entre o gestor e cada comprador, por
 * período, e o «acertado» do gestor. Não entra no custo dos lotes.
 *
 * Quem vê o quê (o banco confere de novo, migração 005):
 *  - comprador: só o extrato dele; lança despesa (sempre a partir da foto),
 *    anula o que ele mesmo lançou enquanto não foi acertado. NÃO vê comissão:
 *    o saldo dele é «despesas a ressarcir − vales»;
 *  - gestor: escolhe o comprador; lança despesa e vale, anula, marca acertado;
 *    vê a linha de comissões e o saldo completo do acerto;
 *  - consulta: vê o mesmo que o gestor; desde a R1A (06/10/2026) dá BAIXA —
 *    registra o pagamento de um acerto já feito (valor, data, comprovante).
 *
 * R1A: o saldo negativo de um acerto (vales acima do que o comprador tinha a
 * receber) aparece como «saldo anterior» no acerto seguinte; e o acerto só
 * fecha os valores que a tela mostrou (senão: «os valores mudaram, confira»).
 *
 * O acerto leva tudo o que está em aberto até o «até». Por isso, além do
 * período, a tela mostra o bloco «Em aberto de antes do período» (lançado
 * atrasado ou que ficou de fora de um acerto) e o soma no saldo do acerto.
 *
 * R2B (revisão de 06/10/2026): a comissão tem o nome fixo de todas as telas —
 * «Comissão do comprador (você paga)»; a janela do acerto divide os valores do
 * MESMO jeito da tela (período, de antes do período, saldo anterior); depois do
 * acerto a tela continua no comprador escolhido, e o app lembra o último
 * comprador aberto (neste aparelho).
 */
import { useEffect, useRef, useState } from 'react'
import { abrirComprovante, ArquivoRecusado, guardarArquivo } from '../dados/arquivos'
import {
  anularAcerto, anularLancamento, CATEGORIAS_DE_DESPESA, emAberto, estaAcertado, lancar, marcarAcertado, porContaInicial, previaDeComissoes,
  registrarPagamentoDoAcerto, resumir, saldoDoAcerto, usarExtrato, type Acerto, type Lancamento, type PorConta, type PreviaDeComissoes, type Resumo,
} from '../dados/extrato'
import { Recusa, SemSinal, type Eu } from '../dados/sessao'
import { agora, dataBR, hoje, lerNumero, novoId, quando, reais } from '../dominio/numeros'
import { avisar, Campo, Dica, Janela, pedirMotivo, Segmento, Tarja } from '../ui'
import { NotasLigadas } from './Notas'
import { pode } from '../dominio/permissoes'
import { NOME_COMISSAO_COMPRADOR } from '../dominio/tipos'

/** R2B · o último comprador aberto no extrato, lembrado neste aparelho (por pessoa) */
const chaveDoUltimo = (pessoaId: string) => `pesagem.extrato.comprador.${pessoaId}`
function lerUltimo(pessoaId: string): string {
  try { return localStorage.getItem(chaveDoUltimo(pessoaId)) ?? '' } catch { return '' }
}
function guardarUltimo(pessoaId: string, compradorId: string): void {
  try { localStorage.setItem(chaveDoUltimo(pessoaId), compradorId) } catch { /* sem disco: só não lembra */ }
}

/** primeiro e último dia do mês de uma data «2026-10-04», com deslocamento de meses */
function mes(base: string, deslocar = 0): [string, string] {
  const [a, m] = base.split('-').map(Number) as [number, number]
  const ini = new Date(a, m - 1 + deslocar, 1)
  const fim = new Date(a, m + deslocar, 0)
  const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return [f(ini), f(fim)]
}

function diaSeguinte(d: string): string {
  const [a, m, dd] = d.split('-').map(Number) as [number, number, number]
  const x = new Date(a, m - 1, dd + 1)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}

export function Extrato({ eu }: { eu: Eu }) {
  const ext = usarExtrato()
  const papel = eu.pessoa.papel
  const vejoTodos = papel === 'gestor' || papel === 'consulta'
  // 3E: a matriz única — vale e acerto só do gestor
  const gestor = pode(papel, 'vale') && pode(papel, 'acerto')
  // R2B: abre no último comprador escolhido (e fica nele depois do acerto)
  const [escolhido, setEscolhidoAqui] = useState(() => (vejoTodos ? lerUltimo(eu.pessoa.id) : ''))
  const setEscolhido = (id: string) => { setEscolhidoAqui(id); guardarUltimo(eu.pessoa.id, id) }
  const valido = ext.compradores.some((c) => c.id === escolhido)
  const compradorId = vejoTodos ? ((valido ? escolhido : '') || ext.compradores[0]?.id || '') : eu.pessoa.id
  const comprador = vejoTodos ? ext.compradores.find((c) => c.id === compradorId) : { id: eu.pessoa.id, nome: eu.pessoa.nome, ativa: true }
  const [periodo, setPeriodo] = useState<[string, string]>(() => mes(hoje()))
  const [de, ate] = periodo

  const [foto, setFoto] = useState<File | null>(null)
  const [fazendoVale, setFazendoVale] = useState(false)
  const [acertando, setAcertando] = useState(false)
  const camera = useRef<HTMLInputElement>(null)
  const galeria = useRef<HTMLInputElement>(null)

  const doComprador = ext.lancamentos.filter((l) => l.compradorId === compradorId)
  const noPeriodo = doComprador
    .filter((l) => l.data >= de && l.data <= ate)
    .sort((a, b) => (a.data === b.data ? b.lancadoEm.localeCompare(a.lancadoEm) : b.data.localeCompare(a.data)))
  // o que o acerto leva além do período: em aberto, com data antes do «de»
  const deAntes = doComprador
    .filter((l) => l.data < de && emAberto(l, ext.acertos))
    .sort((a, b) => b.data.localeCompare(a.data))
  const acertos = ext.acertos.filter((a) => a.compradorId === compradorId).sort((a, b) => b.ate.localeCompare(a.ate))
  const ultimoAcerto = acertos.find((a) => !a.anulacao)
  const r = resumir(doComprador, ext.acertos, de, ate)
  const pendentes = new Set(ext.pendentes)

  // comissão: só gestor e consulta, pedida ao servidor (ponto de ligação com a 2D)
  const [comissoes, setComissoes] = useState<PreviaDeComissoes | undefined | 'carregando'>(undefined)
  // R1A: «os valores mudaram» no acerto pede a prévia de novo
  const [recarga, setRecarga] = useState(0)
  useEffect(() => {
    if (!vejoTodos || !compradorId) return
    let vivo = true
    setComissoes('carregando')
    previaDeComissoes(compradorId, de, ate).then((v) => { if (vivo) setComissoes(v) }).catch(() => { if (vivo) setComissoes(undefined) })
    return () => { vivo = false }
  }, [vejoTodos, compradorId, de, ate, recarga])
  const previa = typeof comissoes === 'object' ? comissoes : null
  const comissaoAntes = previa?.antes ?? 0
  const temAntes = r.antes.lancamentos > 0 || (vejoTodos && comissaoAntes > 0)
  // R1A: o saldo negativo do acerto anterior entra neste — a conta fica no QuadroDoSaldo (tela e janela iguais)

  function escolherFoto(lista: FileList | null) {
    const a = lista?.[0]
    if (a) setFoto(a)
  }

  function anular(l: Lancamento) {
    const motivo = pedirMotivo(`Anular ${l.tipo === 'adiantamento' ? 'o vale' : 'a despesa'} de ${reais(l.valor)} (${dataBR(l.data)})?\n\nO lançamento continua no extrato, riscado, com o motivo. Qual o motivo?`, true)
    if (motivo === null) return
    anularLancamento(l.id, motivo, eu.pessoa.id)
    avisar('Anulado — vai no próximo Sincronizar')
  }

  const podeAnular = (l: Lancamento) => emAberto(l, ext.acertos)
    && pode(papel, 'anularLancamento', { seu: l.lancadoPor === eu.pessoa.id })

  return (
    <div>
      <h2 className="secao">Extrato de viagem</h2>
      <Dica>Despesas de viagem e vales entre {vejoTodos ? 'a empresa e o comprador' : 'você e a empresa'}. Não entram no custo dos lotes.</Dica>

      {vejoTodos && (
        ext.compradores.length
          ? <label className="campo"><span>Comprador</span>
              <select value={compradorId} onChange={(e) => setEscolhido(e.target.value)}>
                {ext.compradores.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.ativa ? '' : ' (saiu)'}</option>)}
              </select></label>
          : <Tarja>Nenhum comprador neste aparelho ainda. Toque em «Sincronizar» na tela Fichas, com sinal, para carregar a lista.</Tarja>
      )}

      <div className="cartao">
        <div className="linha">
          <Campo rotulo="De" tipo="date" valor={de} mudar={(v) => v && setPeriodo([v, ate])} />
          <Campo rotulo="Até" tipo="date" valor={ate} mudar={(v) => v && setPeriodo([de, v])} />
        </div>
        <div className="linha" style={{ marginTop: 8 }}>
          <button className="mini" onClick={() => setPeriodo(mes(hoje()))}>Este mês</button>
          <button className="mini" onClick={() => setPeriodo(mes(hoje(), -1))}>Mês passado</button>
          {ultimoAcerto && <button className="mini" onClick={() => setPeriodo([diaSeguinte(ultimoAcerto.ate), hoje()])}>Desde o último acerto</button>}
        </div>
      </div>

      <QuadroDoSaldo r={r} comissoes={comissoes} vejoTodos={vejoTodos} />
      {vejoTodos && !!previa?.avisos.length && <Tarja>{previa.avisos.join(' ')}</Tarja>}

      {pode(papel, 'despesa') && (vejoTodos ? !!comprador : true) && (
        <>
          <button className="botao grande" onClick={() => camera.current?.click()}>📷 Nova despesa — tirar foto do comprovante</button>
          <button className="botao fantasma pequeno" onClick={() => galeria.current?.click()}>ou escolher uma foto/PDF já salvo</button>
          <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { escolherFoto(e.target.files); e.target.value = '' }} />
          <input ref={galeria} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { escolherFoto(e.target.files); e.target.value = '' }} />
        </>
      )}
      {gestor && comprador && (
        <>
          <button className="botao fantasma" onClick={() => setFazendoVale(true)}>+ Vale (adiantamento)</button>
          <button className="botao" onClick={() => setAcertando(true)}>✔ Marcar acertado este período</button>
        </>
      )}

      <h2 className="secao">Lançamentos de {dataBR(de)} a {dataBR(ate)}</h2>
      {noPeriodo.length ? noPeriodo.map((l) => (
        <LinhaDoExtrato key={l.id} l={l} acertado={estaAcertado(l, ext.acertos)} pendente={pendentes.has(l.id)}
          anular={podeAnular(l) ? () => anular(l) : undefined} />
      )) : <div className="cartao vazio">Nenhum lançamento neste período.</div>}

      {temAntes && (
        <>
          <h2 className="secao">Em aberto de antes do período</h2>
          <Dica>Lançado atrasado (sincronizou depois do acerto do mês dele) ou que ficou de fora de um acerto.
            Entra no próximo acerto, junto com o período.</Dica>
          {deAntes.map((l) => (
            <LinhaDoExtrato key={l.id} l={l} acertado={false} pendente={pendentes.has(l.id)}
              anular={podeAnular(l) ? () => anular(l) : undefined} />
          ))}
          {vejoTodos && comissaoAntes > 0 && (
            <div className="cartao"><div className="tl"><span>Comissão do comprador — fichas de antes do período</span><b>{reais(comissaoAntes)}</b></div></div>
          )}
        </>
      )}

      {acertos.length > 0 && (
        <>
          <h2 className="secao">Acertos</h2>
          {acertos.map((a) => <LinhaDoAcerto key={a.id} a={a} vejoTodos={vejoTodos} gestor={gestor} podePagar={pode(papel, 'pagarAcerto')} />)}
        </>
      )}

      {foto && comprador && (
        <FormDespesa foto={foto} eu={eu} compradorId={comprador.id} compradorNome={comprador.nome}
          fechar={() => setFoto(null)} />
      )}
      {fazendoVale && comprador && (
        <FormVale eu={eu} compradorId={comprador.id} compradorNome={comprador.nome} fechar={() => setFazendoVale(false)} />
      )}
      {acertando && comprador && (
        <FormAcerto compradorId={comprador.id} compradorNome={comprador.nome} de={de} ate={ate}
          r={r} comissoes={comissoes} fechar={() => setAcertando(false)} recarregar={() => setRecarga((x) => x + 1)}
          // R2B: depois do acerto a tela fica NESTE comprador, com a prévia refeita (o acerto aparece em «Acertos»)
          acertou={() => { setEscolhido(comprador.id); setRecarga((x) => x + 1) }} />
      )}
    </div>
  )
}

/**
 * R2B · O saldo do acerto, dividido do MESMO jeito na tela e na janela do
 * acerto: o período (despesas, vales, comissão do comprador), o que está em
 * aberto de antes do período, o saldo anterior e o saldo.
 */
function QuadroDoSaldo({ r, comissoes, vejoTodos }: { r: Resumo; comissoes: PreviaDeComissoes | undefined | 'carregando'; vejoTodos: boolean }) {
  const previa = typeof comissoes === 'object' ? comissoes : null
  const comissaoNum = previa?.total ?? 0
  const comissaoAntes = previa?.antes ?? 0
  const temAntes = r.antes.lancamentos > 0 || (vejoTodos && comissaoAntes > 0)
  const saldoAnterior = vejoTodos ? previa?.saldoAnterior ?? 0 : 0
  const saldo = Math.round((r.aAcertar.saldoSemComissao + (vejoTodos ? comissaoNum : 0) + saldoAnterior) * 100) / 100
  return (
    <div className="resultado">
      <div className="tl"><span>Despesas a ressarcir</span><b>{reais(r.aRessarcir)}</b></div>
      <div className="tl"><span>Vales (adiantamentos)</span><b>− {reais(r.adiantamentos)}</b></div>
      {vejoTodos && (
        <div className="tl"><span>{NOME_COMISSAO_COMPRADOR}</span>
          <b>{comissoes === 'carregando' ? '…' : comissoes === undefined ? 'sem sinal' : reais(comissaoNum - comissaoAntes)}</b></div>
      )}
      {temAntes && (
        <div className="tl"><span>Em aberto de antes do período</span>
          <b>{reais(Math.round((r.antes.saldoSemComissao + (vejoTodos ? comissaoAntes : 0)) * 100) / 100)}</b></div>
      )}
      {saldoAnterior !== 0 && (
        <div className="tl"><span>Saldo anterior (vales acima do acerto passado)</span><b>{reais(saldoAnterior)}</b></div>
      )}
      {vejoTodos
        ? <div className="tl total"><span>Saldo do acerto</span><b>{reais(saldo)}</b></div>
        : <div className="tl total"><span>Despesas − vales</span><b>{reais(saldo)}</b></div>}
      <div className="mini-dica">
        Em aberto no período ({r.lancamentos} lançamento{r.lancamentos === 1 ? '' : 's'}): não anulado e ainda não acertado.
        {temAntes && ' O saldo soma também o que ficou em aberto de antes do período.'}
        {' '}{saldo >= 0
          ? (vejoTodos ? 'Positivo: a empresa paga ao comprador.' : 'Positivo: a empresa te devolve.')
          : (vejoTodos ? 'Negativo: os vales passaram do que há a receber.' : 'Negativo: os vales passaram das despesas.')}
      </div>
      {r.doComprador > 0 && <div className="mini-dica">Despesas por conta do próprio comprador (só registro, fora do saldo): {reais(r.doComprador)}</div>}
    </div>
  )
}

function LinhaDoExtrato({ l, acertado, pendente, anular }: { l: Lancamento; acertado: boolean; pendente: boolean; anular?: () => void }) {
  const vale = l.tipo === 'adiantamento'
  return (
    <div className="cartao" style={l.anulacao || l.recusa ? { opacity: 0.6 } : undefined}>
      <div className="tl">
        <span><b>{vale ? 'Vale' : l.categoria || 'Despesa'}</b> · {dataBR(l.data)}</span>
        <b style={l.anulacao || l.recusa ? { textDecoration: 'line-through' } : undefined}>{vale ? '− ' : ''}{reais(l.valor)}</b>
      </div>
      {l.descricao && <div>{l.descricao}</div>}
      <div className="mini-dica">
        {vale ? 'Adiantamento da empresa' : l.porConta === 'gestor' ? 'A empresa paga (entra no acerto)' : 'Por conta do comprador (só registro)'}
        {' · lançado por '}{l.lancadoPorNome || 'gestor'}
        {pendente && <span className="etiqueta aberta">a enviar</span>}
        {acertado && <span className="etiqueta ok">acertado</span>}
        {l.anulacao && <span className="etiqueta cancelada">anulado</span>}
        {l.recusa && <span className="etiqueta cancelada">recusado pelo servidor</span>}
      </div>
      {l.recusa && <div className="mini-dica">Não entrou no extrato e fica fora das somas: {l.recusa.motivo} Fica só neste aparelho.</div>}
      {l.anulacao && <div className="mini-dica">Motivo da anulação: {l.anulacao.motivo} · {quando(l.anulacao.em)}</div>}
      <div className="linha" style={{ marginTop: 6 }}>
        {l.comprovanteId && (
          <button className="mini" onClick={async () => {
            const erro = await abrirComprovante(l.comprovanteId!)
            if (erro) window.alert(erro)
          }}>📎 Ver comprovante</button>
        )}
        {anular && <button className="mini" onClick={anular}>Anular</button>}
      </div>
      {/* D4: a nota fiscal da despesa (cupom do almoço, do diesel…) */}
      {!vale && !l.recusa && <NotasLigadas ligacao={{ tipo: 'despesa', id: l.id }} compacto podeAdicionar={!l.anulacao} />}
    </div>
  )
}

function LinhaDoAcerto({ a, vejoTodos, gestor, podePagar }: { a: Acerto; vejoTodos: boolean; gestor: boolean; podePagar: boolean }) {
  const [indo, setIndo] = useState(false)
  const [pagando, setPagando] = useState(false)
  const saldoSem = Math.round((a.despesas - a.adiantamentos) * 100) / 100
  async function anular() {
    const motivo = pedirMotivo(`Anular o acerto de ${dataBR(a.de)} a ${dataBR(a.ate)}?\n\nOs lançamentos dele voltam a ficar em aberto. Qual o motivo?`, true)
    if (motivo === null) return
    setIndo(true)
    try {
      await anularAcerto(a.id, motivo)
      avisar('Acerto anulado')
    } catch (e) {
      window.alert(e instanceof SemSinal ? 'Sem internet agora. Anular acerto precisa de sinal.' : e instanceof Recusa ? e.message : 'Não deu para anular.')
    } finally { setIndo(false) }
  }
  return (
    <div className="cartao" style={a.anulacao ? { opacity: 0.6 } : undefined}>
      <div className="tl"><span><b>{dataBR(a.de)} a {dataBR(a.ate)}</b></span>
        {a.anulacao ? <span className="etiqueta cancelada">anulado</span> : <span className="etiqueta ok">acertado</span>}</div>
      <div className="tl"><span>Despesas a ressarcir</span><b>{reais(a.despesas)}</b></div>
      <div className="tl"><span>Vales</span><b>− {reais(a.adiantamentos)}</b></div>
      {vejoTodos && (
        <>
          <div className="tl"><span>{NOME_COMISSAO_COMPRADOR}</span><b>{a.comissoes === null || a.comissoes === undefined ? 'não calculadas' : reais(a.comissoes)}</b></div>
          {!!a.saldoAnterior && <div className="tl"><span>Saldo anterior</span><b>{reais(a.saldoAnterior)}</b></div>}
          <div className="tl total"><span>Saldo acertado</span><b>{reais(saldoDoAcerto(a))}</b></div>
          {saldoDoAcerto(a) < 0 && !a.anulacao && <div className="mini-dica">Negativo: passa para o próximo acerto como saldo anterior.</div>}
        </>
      )}
      {!vejoTodos && <div className="tl total"><span>Despesas − vales</span><b>{reais(saldoSem)}</b></div>}
      <div className="mini-dica">Marcado em {quando(a.feitoEm)}{a.obs ? ` · ${a.obs}` : ''}</div>
      {a.valorPago != null && (
        <div className="mini-dica">Pago {reais(a.valorPago)}{a.pagoEm ? ` em ${dataBR(a.pagoEm)}` : ''}{a.pagoPorNome ? ` · lançado por ${a.pagoPorNome}` : ''}</div>
      )}
      {a.anulacao && <div className="mini-dica">Motivo da anulação: {a.anulacao.motivo} · {quando(a.anulacao.em)}</div>}
      <div className="linha" style={{ marginTop: 6 }}>
        {a.comprovanteId && (
          <button className="mini" onClick={async () => {
            const erro = await abrirComprovante(a.comprovanteId!)
            if (erro) window.alert(erro)
          }}>📎 Comprovante do acerto</button>
        )}
        {a.pagoComprovanteId && (
          <button className="mini" onClick={async () => {
            const erro = await abrirComprovante(a.pagoComprovanteId!)
            if (erro) window.alert(erro)
          }}>📎 Comprovante do pagamento</button>
        )}
        {podePagar && !a.anulacao && a.valorPago == null && <button className="mini" onClick={() => setPagando(true)}>Registrar pagamento</button>}
        {gestor && !a.anulacao && <button className="mini" disabled={indo} onClick={anular}>Anular acerto</button>}
      </div>
      {pagando && <FormPagamentoDoAcerto a={a} sugestao={vejoTodos ? Math.max(0, saldoDoAcerto(a)) : Math.max(0, saldoSem)} fechar={() => setPagando(false)} />}
    </div>
  )
}

/** R1A · A baixa do acerto: quanto foi pago ao comprador, quando, e o comprovante (gestor ou Consulta, com sinal). */
function FormPagamentoDoAcerto({ a, sugestao, fechar }: { a: Acerto; sugestao: number; fechar: () => void }) {
  const [valor, setValor] = useState(sugestao > 0 ? String(sugestao).replace('.', ',') : '')
  const [pagoEm, setPagoEm] = useState(hoje())
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [indo, setIndo] = useState(false)
  const entrada = useRef<HTMLInputElement>(null)
  async function salvar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor pago.'); return }
    if (!pagoEm) { window.alert('Informe a data do pagamento.'); return }
    setIndo(true)
    try {
      let comprovanteId: string | null = null
      if (arquivo) {
        comprovanteId = novoId()
        await guardarArquivo(comprovanteId, arquivo)
      }
      await registrarPagamentoDoAcerto(a.id, Math.round(v * 100) / 100, pagoEm, comprovanteId)
      avisar('Pagamento do acerto registrado')
      fechar()
    } catch (e) {
      window.alert(e instanceof SemSinal ? 'Sem internet agora. Registrar o pagamento do acerto precisa de sinal.' : e instanceof Recusa || e instanceof ArquivoRecusado ? e.message : 'Não deu para registrar o pagamento.')
    } finally { setIndo(false) }
  }
  return (
    <Janela titulo={`Pagamento do acerto de ${dataBR(a.de)} a ${dataBR(a.ate)}`} fechar={fechar}>
      <label className="campo"><span>Valor pago (R$)</span>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="ex.: 1.250,00" autoFocus /></label>
      <Campo rotulo="Pago em" tipo="date" valor={pagoEm} mudar={setPagoEm} />
      <button className="botao fantasma pequeno" onClick={() => entrada.current?.click()}>
        {arquivo ? `📎 ${arquivo.name}` : '📎 Anexar comprovante (opcional)'}</button>
      <input ref={entrada} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); e.target.value = '' }} />
      <Dica>Fica no nome de quem lançou. Registra uma vez só: se errar, o gestor anula o acerto e faz de novo. O comprador vê o pagamento e o comprovante.</Dica>
      <button className="botao grande" disabled={indo} onClick={salvar}>{indo ? 'Registrando…' : 'Registrar pagamento'}</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

/** Miniatura da foto escolhida (PDF mostra só o nome). */
function Miniatura({ arquivo }: { arquivo: File }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!arquivo.type.startsWith('image/')) return
    const u = URL.createObjectURL(arquivo)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [arquivo])
  return url
    ? <img src={url} alt="comprovante" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 10, background: '#000' }} />
    : <div className="mini-dica">📎 {arquivo.name}</div>
}

function FormDespesa({ foto, eu, compradorId, compradorNome, fechar }: { foto: File; eu: Eu; compradorId: string; compradorNome: string; fechar: () => void }) {
  const [valor, setValor] = useState('')
  const [data, setData] = useState(hoje())
  const [categoria, setCategoria] = useState('')
  const [descricao, setDescricao] = useState('')
  // 3C: o padrão vem do vínculo do comprador (contratado → a empresa; comissionado
  // → ele). Sem vínculo, ninguém escolhe por ele: fica em branco e o app pergunta.
  const [porConta, setPorConta] = useState<PorConta | ''>(() => porContaInicial(eu, compradorId) ?? '')
  const [indo, setIndo] = useState(false)
  const souComprador = eu.pessoa.papel === 'comprador'

  async function salvar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor da despesa.'); return }
    if (!categoria) { window.alert('Escolha o tipo da despesa (almoço, combustível…).'); return }
    if (!data) { window.alert('Informe a data.'); return }
    if (!porConta) { window.alert('Escolha quem paga esta despesa: a empresa ou o comprador.'); return }
    setIndo(true)
    try {
      const fotoId = novoId()
      // a foto é guardada já comprimida: tipo e tamanho são os do guardado
      const guardada = await guardarArquivo(fotoId, foto)
      lancar({
        id: novoId(), compradorId, compradorNome, tipo: 'despesa', data, valor: Math.round(v * 100) / 100,
        categoria, descricao: descricao.trim(), porConta, comprovanteId: fotoId,
        comprovante: { id: fotoId, nome: foto.name, tipo: guardada.type, tamanho: guardada.size },
        lancadoPor: eu.pessoa.id, lancadoPorNome: eu.pessoa.nome, lancadoEm: agora(), anulacao: null, acertoId: null,
      })
      avisar('Despesa guardada — vai no próximo Sincronizar')
      fechar()
    } catch (e) {
      window.alert(e instanceof ArquivoRecusado ? e.message : 'Não foi possível guardar a foto neste aparelho.')
    } finally { setIndo(false) }
  }

  return (
    <Janela titulo={`Nova despesa${souComprador ? '' : ` — ${compradorNome}`}`} fechar={fechar}>
      <Miniatura arquivo={foto} />
      <label className="campo"><span>Valor (R$)</span>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="ex.: 45,90" autoFocus /></label>
      <Campo rotulo="Data" tipo="date" valor={data} mudar={setData} />
      <div className="campo"><span>Tipo</span></div>
      <div className="categorias" style={{ marginBottom: 10 }}>
        {CATEGORIAS_DE_DESPESA.map((c) => (
          <button key={c} type="button" className={`mini${categoria === c ? ' on' : ''}`}
            style={categoria === c ? { background: 'var(--verde)', color: 'var(--verde-texto)' } : undefined}
            onClick={() => setCategoria(c)}>{c}</button>
        ))}
      </div>
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: posto da BR, pneu furado" />
      <div className="campo"><span>Quem paga esta despesa?</span></div>
      <Segmento<PorConta | ''> valor={porConta} mudar={setPorConta}
        opcoes={[['gestor', 'A empresa'], ['comprador', souComprador ? 'Eu mesmo' : 'O comprador']]} />
      <Dica>{porConta === 'gestor'
        ? 'A empresa paga: o valor entra no acerto e volta para o comprador.'
        : porConta === 'comprador'
          ? 'Fica por conta do comprador: só registro, não entra no acerto.'
          : `Escolha uma das duas. Para o app já vir com a resposta, ${souComprador ? 'peça ao gestor para marcar o seu vínculo' : 'marque o vínculo do comprador'} na Equipe (contratado ou comissionado).`}</Dica>
      <button className="botao grande" disabled={indo} onClick={salvar}>{indo ? 'Guardando…' : 'Guardar despesa'}</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

function FormVale({ eu, compradorId, compradorNome, fechar }: { eu: Eu; compradorId: string; compradorNome: string; fechar: () => void }) {
  const [valor, setValor] = useState('')
  const [data, setData] = useState(hoje())
  const [descricao, setDescricao] = useState('')
  function salvar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor do vale.'); return }
    if (!data) { window.alert('Informe a data.'); return }
    lancar({
      id: novoId(), compradorId, compradorNome, tipo: 'adiantamento', data, valor: Math.round(v * 100) / 100,
      categoria: 'Vale', descricao: descricao.trim(), porConta: 'gestor', comprovanteId: null,
      lancadoPor: eu.pessoa.id, lancadoPorNome: eu.pessoa.nome, lancadoEm: agora(), anulacao: null, acertoId: null,
    })
    avisar('Vale guardado — vai no próximo Sincronizar')
    fechar()
  }
  return (
    <Janela titulo={`Vale para ${compradorNome}`} fechar={fechar}>
      <label className="campo"><span>Valor (R$)</span>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="ex.: 500" autoFocus /></label>
      <Campo rotulo="Data" tipo="date" valor={data} mudar={setData} />
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: PIX para a viagem a Marabá" />
      <Dica>O vale é descontado no acerto. Pode ser dado fora de viagem também.</Dica>
      <button className="botao grande" onClick={salvar}>Guardar vale</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

function FormAcerto(p: {
  compradorId: string; compradorNome: string; de: string; ate: string
  r: Resumo; comissoes: PreviaDeComissoes | undefined | 'carregando'; fechar: () => void
  /** R1A: pede a prévia de novo (os valores mudaram enquanto a janela estava aberta) */
  recarregar: () => void
  /** R2B: o acerto foi feito — a tela fica no mesmo comprador */
  acertou: () => void
}) {
  const [obs, setObs] = useState('')
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [valorPago, setValorPago] = useState('')
  const [pagoEm, setPagoEm] = useState(hoje())
  const [indo, setIndo] = useState(false)
  const entrada = useRef<HTMLInputElement>(null)
  // o acerto fecha o período E o que está em aberto de antes dele
  const t = p.r.aAcertar
  const previa = typeof p.comissoes === 'object' ? p.comissoes : null
  const comissao = previa?.total ?? 0
  const anterior = previa?.saldoAnterior ?? 0
  const saldo = Math.round((t.saldoSemComissao + comissao + anterior) * 100) / 100

  async function confirmar() {
    if (!previa) { window.alert('Sem sinal para calcular as comissões. O acerto precisa de internet.'); return }
    // R1A: «valor pago» e «pago em» são opcionais; se tiver valor, tem data
    const pago = valorPago.trim() ? lerNumero(valorPago) : null
    if (valorPago.trim() && (pago === null || pago <= 0)) { window.alert('Confira o valor pago (ou deixe em branco).'); return }
    if (pago !== null && !pagoEm) { window.alert('Informe a data do pagamento.'); return }
    setIndo(true)
    try {
      let comprovanteId: string | null = null
      if (arquivo) {
        comprovanteId = novoId()
        await guardarArquivo(comprovanteId, arquivo)
      }
      // os totais que esta janela mostrou: o servidor não fecha outros
      const mostrado = { despesas: t.aRessarcir, adiantamentos: t.adiantamentos, comissoes: comissao, saldoAnterior: anterior }
      const r = await marcarAcertado({
        id: novoId(), compradorId: p.compradorId, de: p.de, ate: p.ate, obs: obs.trim(), comprovanteId, mostrado,
        valorPago: pago === null ? null : Math.round(pago * 100) / 100, pagoEm: pago === null ? null : pagoEm,
      })
      avisar(`Acertado · ${r.lancamentos} lançamento${r.lancamentos === 1 ? '' : 's'}`)
      p.acertou()
      p.fechar()
    } catch (e) {
      // R1A: a tela passa a mostrar os valores novos; a pessoa confere e confirma de novo
      if (e instanceof Recusa && e.dados?.['codigo'] === 'valores_mudaram') p.recarregar()
      window.alert(e instanceof SemSinal ? 'Sem internet agora. Marcar acertado precisa de sinal.' : e instanceof Recusa || e instanceof ArquivoRecusado ? e.message : 'Não deu para marcar o acerto.')
    } finally { setIndo(false) }
  }

  return (
    <Janela titulo={`Acerto com ${p.compradorNome}`} fechar={p.fechar}>
      <div className="mini-dica">Período de {dataBR(p.de)} a {dataBR(p.ate)} · {t.lancamentos} lançamento(s) em aberto neste aparelho
        {p.r.antes.lancamentos > 0 && <>, {p.r.antes.lancamentos} de antes do período</>}</div>
      {/* R2B: os valores divididos do mesmo jeito da tela */}
      <QuadroDoSaldo r={p.r} comissoes={p.comissoes} vejoTodos />
      {!!previa?.avisos.length && <Tarja>{previa.avisos.join(' ')}</Tarja>}
      {saldo < 0 && <div className="mini-dica">Negativo: os vales passaram do que o comprador tinha a receber. Passa para o próximo acerto como saldo anterior.</div>}
      <Dica>O app primeiro envia o que está neste aparelho e depois fecha no servidor tudo o que está em aberto até {dataBR(p.ate)},
        inclusive o de antes do período. O que outros aparelhos ainda não enviaram fica em aberto para o próximo acerto, mesmo
        com data deste período. Depois de acertado, nada disso se anula — só o acerto inteiro.</Dica>
      <Campo rotulo="Observação" valor={obs} mudar={setObs} dica="ex.: PIX em 04/10" />
      <div className="linha">
        <label className="campo"><span>Valor pago (opcional)</span>
          <input inputMode="decimal" value={valorPago} onChange={(e) => setValorPago(e.target.value)} placeholder={saldo > 0 ? reais(saldo) : 'R$'} /></label>
        <Campo rotulo="Pago em" tipo="date" valor={pagoEm} mudar={setPagoEm} />
      </div>
      <button className="botao fantasma pequeno" onClick={() => entrada.current?.click()}>
        {arquivo ? `📎 ${arquivo.name}` : '📎 Anexar comprovante do acerto (opcional)'}</button>
      <input ref={entrada} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); e.target.value = '' }} />
      <button className="botao grande" disabled={indo} onClick={confirmar}>{indo ? 'Acertando…' : 'Confirmar acerto'}</button>
      <button className="botao fantasma" onClick={p.fechar}>Cancelar</button>
    </Janela>
  )
}
